// La card "Formato de descarga" de Configuración → Funciones avanzadas —
// ADR-2026-09-14-el-formato-de-descarga-vive-en-funciones-avanzadas y
// ADR-2026-09-14-varios-formatos-de-descarga.
//
// Tiene la misma forma que las otras cards de la sección —icono, nombre, descripción e
// interruptor— y, SÓLO con el interruptor prendido, lo que las otras no tienen: la lista
// de formatos, cada uno con sus campos y su "usar siempre". Apagada no muestra nada más:
// una card con seis campos atenuados es ruido para quien no la usa.
//
// Dos cosas que la separan de sus vecinas y están dichas en la card:
//
//   · Se guarda por MARCA (`brand_profiles.descarga_config`) y no por empresa, igual que
//     la marca de agua: una empresa con dos marcas puede vender en dos tiendas que piden
//     medidas distintas. El resto de la sección son llaves de `company_profiles`.
//   · El prendido/apagado es el `activo` de esa misma config y NO una llave nueva de
//     `funciones_avanzadas`: dos interruptores para lo mismo se desincronizan, y el que
//     manda al descargar es este.

import React, { useEffect, useRef, useState } from 'react';
import { Crop, Plus, Trash2 } from 'lucide-react';
import { useBrandProfile } from '../../hooks/useBrandProfile';
import { Interruptor } from './Interruptor';
import {
  ANCHO_MAX,
  ANCHO_MIN,
  PESO_MAX_KB,
  PESO_MIN_KB,
  defaultConfigDeDescarga,
  fetchConfigDeDescarga,
  guardarConfigDeDescarga,
  nuevoPerfil,
  type ConfigDeDescarga,
  type FormatoDeSalida,
  type PerfilDeDescarga,
} from '../../utils/configDeDescarga';
import { proporcionDe } from '../../utils/formatoDeSalida';

interface Props {
  setError: (error: string | null) => void;
}

const FORMATOS: Array<{ id: FormatoDeSalida; nombre: string; ayuda: string }> = [
  { id: 'webp', nombre: 'WebP', ayuda: 'El más liviano. Lo aceptan todas las tiendas.' },
  { id: 'jpg', nombre: 'JPG', ayuda: 'El más compatible, un poco más pesado.' },
  { id: 'png', nombre: 'PNG', ayuda: 'Sin pérdida y sin tope de peso: pesa mucho más.' },
];

/** Cuánto se espera desde la última tecla antes de guardar los campos. */
const ESPERA_MS = 800;

const ROTULO = 'block text-[13px] font-medium text-neutral-600 mb-1.5';
const CAMPO =
  'w-full h-10 px-3 rounded-md border border-neutral-200 text-sm text-brand-black focus:outline-none focus:border-brand-primary';

export const FormatoDeDescarga: React.FC<Props> = ({ setError }) => {
  const { activeBrand, loading: cargandoMarca } = useBrandProfile();
  const brandId = activeBrand?.id ?? null;
  const [config, setConfig] = useState<ConfigDeDescarga | null>(null);
  const [estado, setEstado] = useState<'quieto' | 'guardando' | 'guardado'>('quieto');
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!brandId) return;
    let vivo = true;
    void fetchConfigDeDescarga(brandId).then((c) => {
      if (!vivo) return;
      // Sin nada guardado, hay un formato de referencia listo para editar pero
      // APAGADO: nada se aplica hasta que alguien prende el interruptor.
      setConfig(c ?? { ...defaultConfigDeDescarga(), activo: false });
    });
    return () => {
      vivo = false;
    };
  }, [brandId]);

  // Un timer pendiente cuando la card se desmonta guardaría sobre un componente muerto.
  useEffect(() => () => void (timer.current && clearTimeout(timer.current)), []);

  const persistir = async (siguiente: ConfigDeDescarga) => {
    if (!brandId) return;
    setEstado('guardando');
    setError(null);
    const r = await guardarConfigDeDescarga(brandId, siguiente);
    if (r.ok) {
      setEstado('guardado');
      setTimeout(() => setEstado('quieto'), 2000);
    } else {
      setEstado('quieto');
      setError(r.error ?? 'No se pudo guardar el formato de descarga.');
    }
  };

  /** Los interruptores guardan al toque; los campos, cuando el usuario deja de escribir. */
  const cambiar = (siguiente: ConfigDeDescarga, alToque: boolean) => {
    setConfig(siguiente);
    if (timer.current) clearTimeout(timer.current);
    if (alToque) void persistir(siguiente);
    else timer.current = setTimeout(() => void persistir(siguiente), ESPERA_MS);
  };

  if (cargandoMarca || !config) return null;

  const activo = config.activo;

  const cambiarPerfil = (id: string, parcial: Partial<PerfilDeDescarga>, alToque: boolean) =>
    cambiar(
      { ...config, perfiles: config.perfiles.map((p) => (p.id === id ? { ...p, ...parcial } : p)) },
      alToque,
    );

  const agregar = () => cambiar({ ...config, perfiles: [...config.perfiles, nuevoPerfil()] }, true);

  const quitar = (id: string) =>
    cambiar(
      {
        ...config,
        perfiles: config.perfiles.filter((p) => p.id !== id),
        // Si era el de "siempre", vuelve a preguntar: no hay a quién señalar.
        siempre: config.siempre === id ? null : config.siempre,
      },
      true,
    );

  const numero = (valor: number, aplicar: (n: number) => void, min: number, max: number) => (
    <input
      type="number"
      min={min}
      max={max}
      value={valor}
      onChange={(e) => {
        const n = Number(e.target.value);
        if (Number.isFinite(n)) aplicar(n);
      }}
      className={CAMPO}
    />
  );

  const bloqueDePerfil = (p: PerfilDeDescarga, i: number) => {
    const proporcion = proporcionDe(p.ancho, p.alto);
    return (
      <div key={p.id} className={i === 0 ? '' : 'mt-5 border-t border-neutral-200 pt-5'}>
        <div className="flex items-end gap-3">
          <div className="flex-1 max-w-sm">
            <label className={ROTULO}>Nombre</label>
            <input
              type="text"
              value={p.nombre}
              placeholder="Tiendanube, Mercado Libre…"
              maxLength={60}
              onChange={(e) => cambiarPerfil(p.id, { nombre: e.target.value }, false)}
              className={CAMPO}
            />
          </div>
          <button
            type="button"
            onClick={() => quitar(p.id)}
            aria-label="Quitar este formato"
            className="inline-flex items-center gap-1.5 h-10 px-3 rounded-md text-sm text-neutral-500 hover:bg-neutral-100 hover:text-brand-black transition-colors"
          >
            <Trash2 className="w-4 h-4" />
            Quitar
          </button>
        </div>

        <div className="grid grid-cols-2 gap-3 max-w-sm mt-4">
          <div>
            <label className={ROTULO}>Ancho (px)</label>
            {numero(p.ancho, (n) => cambiarPerfil(p.id, { ancho: n }, false), ANCHO_MIN, ANCHO_MAX)}
          </div>
          <div>
            <label className={ROTULO}>Alto (px)</label>
            {numero(p.alto, (n) => cambiarPerfil(p.id, { alto: n }, false), ANCHO_MIN, ANCHO_MAX)}
          </div>
        </div>
        <p className="text-[12.5px] text-neutral-500 mt-2">
          {proporcion
            ? `Proporción ${proporcion}. Lo que no tenga esa proporción se recorta al centro.`
            : 'Lo que no tenga esa proporción se recorta al centro.'}
        </p>

        <div className="mt-4">
          <label className={ROTULO}>Formato</label>
          <div className="flex flex-wrap gap-2">
            {FORMATOS.map((f) => (
              <button
                key={f.id}
                type="button"
                onClick={() => cambiarPerfil(p.id, { formato: f.id }, true)}
                className={`py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                  p.formato === f.id
                    ? 'border-[1.5px] border-brand-primary bg-[rgb(var(--brand-primary-rgb)_/_0.04)] text-brand-black'
                    : 'border border-neutral-200 text-neutral-600 hover:bg-neutral-50'
                }`}
              >
                {f.nombre}
              </button>
            ))}
          </div>
          <p className="text-[12.5px] text-neutral-500 mt-2">
            {FORMATOS.find((f) => f.id === p.formato)?.ayuda}
          </p>
        </div>

        {/* El PNG no tiene calidad que bajar: pedirle un peso sería mentir. */}
        {p.formato !== 'png' && (
          <div className="mt-4">
            <label className={ROTULO}>Peso máximo (KB)</label>
            <div className="flex items-center gap-3">
              <div className="w-32">
                {numero(
                  p.pesoMaxKB ?? nuevoPerfil().pesoMaxKB!,
                  (n) => cambiarPerfil(p.id, { pesoMaxKB: n }, false),
                  PESO_MIN_KB,
                  PESO_MAX_KB,
                )}
              </div>
              <label className="flex items-center gap-2 text-sm text-neutral-600 cursor-pointer">
                <input
                  type="checkbox"
                  checked={p.pesoMaxKB === null}
                  onChange={(e) =>
                    cambiarPerfil(
                      p.id,
                      { pesoMaxKB: e.target.checked ? null : nuevoPerfil().pesoMaxKB },
                      true,
                    )
                  }
                  className="w-4 h-4 accent-brand-primary"
                />
                Sin tope
              </label>
            </div>
            <p className="text-[12.5px] text-neutral-500 mt-2">
              Probamos varias calidades hasta que entre. Si con la mejor ya pesa menos, se
              baja así: pesar poco no es un problema.
            </p>
          </div>
        )}

        {/* "Siempre" es de UN formato: prender el de uno apaga el de los demás. */}
        <div className="mt-4 flex items-start gap-4">
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-brand-black">
              Usar este formato siempre, sin preguntar
            </p>
            <p className="mt-1 text-sm leading-relaxed text-neutral-500">
              Todas tus descargas salen así, convertidas y recortadas. Dejalo apagado si
              algunas las querés en el original o en otro formato: así elegís en cada descarga.
            </p>
          </div>
          <Interruptor
            activo={config.siempre === p.id}
            disabled={!brandId}
            etiqueta="Usar este formato siempre"
            onChange={(v) => cambiar({ ...config, siempre: v ? p.id : null }, true)}
          />
        </div>
      </div>
    );
  };

  return (
    <div className="rounded-2xl border border-neutral-200 py-4 px-5">
      <div className="flex items-start gap-4">
        {/* El fondo tenue va inline: `bg-brand-primary/10` no compila. */}
        <span
          className={`shrink-0 grid place-items-center w-10 h-10 rounded-xl ${
            activo ? 'text-brand-primary' : 'bg-neutral-100 text-neutral-400'
          }`}
          style={activo ? { backgroundColor: 'rgb(var(--brand-primary-rgb) / 0.10)' } : undefined}
        >
          <Crop className="w-5 h-5" />
        </span>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-brand-black">Formatos de descarga</p>
          <p className="mt-1 text-sm leading-relaxed text-neutral-500">
            Las medidas y el peso que te pide cada tienda, para no convertir las fotos a mano
            después. Al descargar elegís entre la imagen original y cualquiera de estos.
            {activeBrand?.name ? ` Se guardan para la marca ${activeBrand.name}.` : ''}
          </p>
        </div>
        <Interruptor
          activo={activo}
          disabled={!brandId}
          etiqueta="Formatos de descarga"
          onChange={(v) => cambiar({ ...config, activo: v }, true)}
        />
      </div>

      {/* Apagada, la card termina acá. Prendida, la lista va sangrada hasta donde
          arranca el texto de arriba (icono 40 + gap 16 = pl-14), como los modos. */}
      {activo && (
        <div className="mt-4 border-t border-neutral-200 pt-4 pl-14">
          {config.perfiles.map(bloqueDePerfil)}

          <button
            type="button"
            onClick={agregar}
            className={`inline-flex items-center gap-1.5 py-2 px-4 rounded-md border border-neutral-200 text-sm font-medium text-neutral-600 hover:bg-neutral-50 transition-colors ${
              config.perfiles.length > 0 ? 'mt-5' : ''
            }`}
          >
            <Plus className="w-4 h-4" />
            Agregar formato
          </button>

          {estado !== 'quieto' && (
            <p className="text-[12.5px] text-neutral-400 mt-3">
              {estado === 'guardando' ? 'Guardando…' : 'Guardado'}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default FormatoDeDescarga;
